import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { SubscriptionService } from '../../services/subscription/subscription.service';
import { ToastrService } from 'ngx-toastr';
import * as informativePageActions from './informative-page.actions';
import { DashboardCamelCasePipe } from 'src/app/core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { DashboardSnakeCasePipe } from 'src/app/core/pipes/dashboard-snake-case/dashboard-snake-case.pipe';
import { switchMap, map, catchError, tap, filter } from 'rxjs/operators';
import { of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { CustomRouterNavigatedAction } from 'src/app/core/custom-route-serializer';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { STUDIES_ROUTES } from '../../studies/studies.routes';
import { InformativePagesService } from '../../services/informative-pages/informative-pages.service';
import { InformativePage } from 'src/app/models/informative-page';
import { Router } from '@angular/router';

@Injectable()
export class InformativePageEffects {
  informativePagesRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter((route: CustomRouterNavigatedAction) => {
        return (
          route.payload.routerState.url.endsWith(`/${STUDIES_ROUTES.INFORMATIVE_PAGES}`) ||
          route.payload.routerState.url.endsWith(`/${STUDIES_ROUTES.SESSIONS}`)
        );
      }),
      map((action) =>
        informativePageActions.getInformativePages({
          studyId: action.payload.routerState.params.id,
        })
      )
    )
  );

  getInformativePages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(informativePageActions.getInformativePages),
      switchMap(({ studyId }) => {
        return this.informativePagesService.getInformativePages(studyId).pipe(
          map((informativePages) => {
            const transformedInformativePages = [];
            for (const page of informativePages) {
              const transformedInformativePage: InformativePage = this.dashboardCamelCasePipe.keysToCamelCase(page);
              transformedInformativePages.push(transformedInformativePage);
            }
            return informativePageActions.getInformativePagesSuccess({
              informativePages: transformedInformativePages,
            });
          }),
          catchError((err: HttpErrorResponse) => of(informativePageActions.getInformativePagesError({ err })))
        );
      })
    )
  );

  getInformativePagesError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(informativePageActions.getInformativePagesError),
        tap(({ err }) => this.toastrService.error('Unable to load informative pages'))
      ),
    { dispatch: false }
  );

  createInformativePage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(informativePageActions.createInformativePage),
      switchMap(({ informativePage }) => {
        const informativePageSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(informativePage);
        if (this.router.url.endsWith(`/${STUDIES_ROUTES.INFORMATIVE_PAGES}`)) {
          return this.informativePagesService.createInformativePage(informativePageSnakeCase).pipe(
            map((newInformativePage) => {
              const newInformativePageCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(newInformativePage);
              return informativePageActions.createInformativePageSuccess({
                newInformativePage: newInformativePageCamelCase,
              });
            }),
            catchError((err: HttpErrorResponse) => of(informativePageActions.createInformativePageError({ err })))
          );
        } else {
          return this.subscriptionService.createSubscriptionInformativePage(informativePageSnakeCase).pipe(
            map((newInformativePage) => {
              const newInformativePageCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(newInformativePage);
              return informativePageActions.createInformativePageSuccess({
                newInformativePage: newInformativePageCamelCase,
              });
            }),
            catchError((err: HttpErrorResponse) => of(informativePageActions.createInformativePageError({ err })))
          );
        }
      })
    )
  );

  createInformativePageSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(informativePageActions.createInformativePageSuccess),
        map(({ newInformativePage }) => {
          this.toastrService.success(`Informative page ${newInformativePage.translations[0].translation} saved`);
        })
      ),
    { dispatch: false }
  );

  createInformativePageError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(informativePageActions.createInformativePageError),
        tap(({ err }) => this.toastrService.error(`Unable to save informative page`))
      ),
    { dispatch: false }
  );

  updateInformativePage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(informativePageActions.updateInformativePage),
      switchMap(({ informativePage }) => {
        const informativePageSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(informativePage);
        if (this.router.url.endsWith(`/${STUDIES_ROUTES.INFORMATIVE_PAGES}`)) {
          return this.informativePagesService.updateInformativePage(informativePageSnakeCase).pipe(
            map((newInformativePage) => {
              const newInformativePageCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(newInformativePage);
              return informativePageActions.updateInformativePageSuccess({
                updatedInformativePage: newInformativePageCamelCase,
              });
            }),
            catchError((err: HttpErrorResponse) => of(informativePageActions.updateInformativePageError({ err })))
          );
        } else {
          return this.subscriptionService.updateSubscriptionInformativePage(informativePageSnakeCase).pipe(
            map((newInformativePage) => {
              const newInformativePageCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(newInformativePage);
              return informativePageActions.updateInformativePageSuccess({
                updatedInformativePage: newInformativePageCamelCase,
              });
            }),
            catchError((err: HttpErrorResponse) => of(informativePageActions.updateInformativePageError({ err })))
          );
        }
      })
    )
  );

  updateInformativePageSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(informativePageActions.updateInformativePageSuccess),
        tap(({ updatedInformativePage }) => {
          this.toastrService.success(`Informative page ${updatedInformativePage.translations[0].translation} saved`);
        })
      ),
    { dispatch: false }
  );

  updateInformativePageError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(informativePageActions.updateInformativePageError),
        tap(({ err }) => this.toastrService.error(`Unable to save informative page`))
      ),
    { dispatch: false }
  );

  deleteInformativePage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(informativePageActions.deleteInformativePage),
      switchMap(({ informativePage }) => {
        const informativePageSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(informativePage);
        if (this.router.url.endsWith(`/${STUDIES_ROUTES.INFORMATIVE_PAGES}`)) {
          return this.informativePagesService.deleteInformativePage(informativePageSnakeCase).pipe(
            map(() => {
              return informativePageActions.deleteInformativePageSuccess({
                deletedInformativePage: informativePage,
              });
            }),
            catchError((err: HttpErrorResponse) => of(informativePageActions.deleteInformativePageError({ err })))
          );
        } else {
          return this.subscriptionService.deleteSubscriptionInformativePage(informativePageSnakeCase).pipe(
            map(() => {
              return informativePageActions.deleteInformativePageSuccess({
                deletedInformativePage: informativePage,
              });
            }),
            catchError((err: HttpErrorResponse) => of(informativePageActions.deleteInformativePageError({ err })))
          );
        }
      })
    )
  );

  deleteInformativePageSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(informativePageActions.deleteInformativePageSuccess),
        tap(({ deletedInformativePage }) => {
          this.toastrService.success(`Informative page ${deletedInformativePage.translations[0].translation} deleted!`);
        })
      ),
    { dispatch: false }
  );

  deleteInformativePageError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(informativePageActions.deleteInformativePageError),
        tap(({ err }) => this.toastrService.error(`Unable to remove informative page`))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private subscriptionService: SubscriptionService,
    private informativePagesService: InformativePagesService,
    private toastrService: ToastrService,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private dashboardSnakeCasePipe: DashboardSnakeCasePipe,
    private router: Router
  ) {}
}
